import { useState } from 'react';

import { Stack } from '@mui/material';

import PushScreen from '@/designs-dev/device/PushScreen';
import StatusBar from '@/designs-dev/device/StatusBar';
import Card from '@/components/wrapped/Card';
import ScreenRoot from '@/components/wrapped/ScreenRoot';
import {
  ProfileSection,
  type TabId,
} from '@/karehero-app/architecture/ArchitectureProfile';
import { CARE_HUB } from '@/karehero-app/architecture/Architecture.content';
import { SHORTLISTS } from '@/karehero-app/care/shortlists/fixtures';
import ShortlistsTab from '@/karehero-app/care/shortlists/ShortlistsTab';
import Body from '@/karehero-app/shell/Body';
import Navigation from '@/karehero-app/shell/Navigation';
import StackPage from '@/karehero-app/shell/StackPage';
import {
  ASSESS_ROWS,
  CARE_PROFILE,
  PROFILE_CALLS,
  PROFILE_DOCS,
} from '@/karehero-app/tell-us-once/intake/content';

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

type Section = 'care' | TabId;

// PROPOSED. Care and Profile as one tab: everything about one person, on a
// hub. Arranging care, what they could claim, what we hold, what they've
// handed over and what was said on a call, one short section each, saying
// what matters in it now and opening into the whole of it.
//
// A hub rather than pills: five pills across a phone don't fit, and a hub can
// say something about each part before you open it, where a pill only names
// it. Every section opens into the list the master already draws.
//
// Everything here is the person the bar names. The fixtures are one person's,
// so anyone else switched to reads as just started.
const ArchitectureCare = ({
  person = CARE_PROFILE.person,
  onSwitch,
  onNotifications,
}: {
  /** Who the bar names, and so whose care this is. */
  person?: string;
  /** Someone else picked in the bar's switcher. */
  onSwitch?: (name: string) => void;
  onNotifications?: () => void;
} = {}) => {
  const [open, setOpen] = useState<Section | null>(null);
  const known = person === CARE_PROFILE.person;

  const lead = ASSESS_ROWS.find((r) => r.lead) ?? ASSESS_ROWS[0];
  const facts = CARE_PROFILE.groups.reduce((n, g) => n + g.rows.length, 0);
  const summary: Record<Section, string> = {
    care: plural(SHORTLISTS.length, 'shortlist', 'shortlists'),
    assess:
      lead.answered !== undefined && lead.total !== undefined
        ? `${lead.name}, ${lead.answered} of ${lead.total} answered`
        : lead.name,
    about: plural(facts, 'thing we hold', 'things we hold'),
    docs: plural(PROFILE_DOCS.length, 'document', 'documents'),
    calls: PROFILE_CALLS[0]
      ? `Last: ${PROFILE_CALLS[0].name}`
      : plural(0, 'call', 'calls'),
  };

  return (
    <ScreenRoot relative>
      <StatusBar />
      <Navigation
        title={person}
        onNotifications={onNotifications}
        avatarInitials='OP'
        onSwitch={onSwitch}
      />

      <Body>
        <Stack spacing={1.5}>
          {CARE_HUB.sections.map((s) => (
            <Card
              key={s.id}
              title={s.label}
              titleVariant='h4'
              subtitle={known ? summary[s.id] : CARE_HUB.empty}
              onClick={known ? () => setOpen(s.id) : undefined}
            />
          ))}
        </Stack>
      </Body>

      {open === 'care' && (
        <PushScreen onClose={() => setOpen(null)}>
          {(close) => <ShortlistsTab onBack={close} />}
        </PushScreen>
      )}
      {open && open !== 'care' && (
        <PushScreen onClose={() => setOpen(null)}>
          {(close) => (
            <StackPage
              title={CARE_HUB.sections.find((s) => s.id === open)?.label}
              onBack={close}
            >
              <ProfileSection tab={open} />
            </StackPage>
          )}
        </PushScreen>
      )}
    </ScreenRoot>
  );
};

export default ArchitectureCare;
